import { Injectable, inject, signal, effect } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { AuthService } from './auth.service';
import { FirestoreService } from './firestore.service';
import { UserService } from './user.service';
import { UsuarioBloqueado } from '../models/usuario.interface';
import { EstadisticasService } from './estadisticas.service';

@Injectable({ providedIn: 'root' })
export class BloqueosService {
  private authService = inject(AuthService);
  private firestoreService = inject(FirestoreService);
  private userService = inject(UserService);
  private estadisticasService = inject(EstadisticasService);

  uidsBloqueados = signal<Set<string>>(new Set());
  bloqueadosConPerfil = signal<UsuarioBloqueado[]>([]);

  // Usuarios que me bloquearon a mí
  uidsBloqueadoPor = signal<Set<string>>(new Set());
  bloqueadoPorConPerfil = signal<UsuarioBloqueado[]>([]);

  constructor() {
    // Escuchar colección de bloqueados
    effect(() => {
      const uid = this.authService.uid;
      if (!uid) return;
      console.log('Escuchando bloqueados');

      this.firestoreService.listenCollection<{ bloqueadoEn: Timestamp }>(
        `usuarios/${uid}/bloqueados`,
        async bloqueadosData => {
          console.log('Datos bloqueados recibidos:', bloqueadosData);
          if (!bloqueadosData.length) {
            console.log('No hay usuarios bloqueados');
            this.bloqueadosConPerfil.set([]);
            this.uidsBloqueados.set(new Set());
            return;
          }

          try {
            const resultados = await Promise.all(
              bloqueadosData.map(async b => {
                const perfil = await this.userService.getPerfil(b.uid);
                return {
                  uid: b.uid,
                  nombre: perfil?.nombre ?? 'Desconocido',
                  foto: perfil?.fotos?.find(f => !f.eliminada)?.url ?? '',
                  bloqueadoEn: b.bloqueadoEn
                };
              })
            );

            console.log('Bloqueados con perfil:', resultados);
            this.bloqueadosConPerfil.set(resultados);
            this.uidsBloqueados.set(new Set(resultados.map(b => b.uid)));
          } catch (error) {
            console.error('[BloqueosService] Error cargando perfiles bloqueados:', error);
          }
        }
      );
    });

    // Escuchar colección de bloqueadoPor
    effect(() => {
      const uid = this.authService.uid;
      if (!uid) return;
      console.log('Escuchando bloqueadoPor');

      this.firestoreService.listenCollection<{ bloqueadoEn: Timestamp; creadoPor: string }>(
        `usuarios/${uid}/bloqueadoPor`,
        async bloqueadoPorData => {
          console.log('Datos bloqueadoPor recibidos:', bloqueadoPorData);
          if (!bloqueadoPorData.length) {
            console.log('Nadie me ha bloqueado');
            this.bloqueadoPorConPerfil.set([]);
            this.uidsBloqueadoPor.set(new Set());
            return;
          }

          try {
            const perfilesPor = await Promise.all(
              bloqueadoPorData.map(async b => {
                const perfil = await this.userService.getPerfil(b.uid);
                return {
                  uid: b.uid,
                  nombre: perfil?.nombre ?? 'Desconocido',
                  foto: perfil?.fotos?.find(f => !f.eliminada)?.url ?? '',
                  bloqueadoEn: b.bloqueadoEn
                } as UsuarioBloqueado;
              })
            );

            console.log('Perfiles bloqueadores:', perfilesPor);
            this.bloqueadoPorConPerfil.set(perfilesPor);
            this.uidsBloqueadoPor.set(new Set(perfilesPor.map(p => p.uid)));
            console.log('Bloqueado por:', this.bloqueadoPorConPerfil());
          } catch (error) {
            console.error('[BloqueosService] Error cargando perfiles de bloqueadores:', error);
          }
        }
      );
    });
  }

  async bloquear(bloqueadoUid: string): Promise<void> {
    const uid = this.authService.uid;
    if (!uid) throw new Error('Usuario no autenticado');

    try {
      const ts = Timestamp.now();

      await this.firestoreService.setDoc(`usuarios/${uid}/bloqueados`, bloqueadoUid, { bloqueadoEn: ts });
      await this.firestoreService.setDoc(`usuarios/${bloqueadoUid}/bloqueadoPor`, uid, {
        bloqueadoEn: ts,
        creadoPor: uid
      });

      await this.estadisticasService.incrementarCampo('totalBloqueos', 1);
    } catch (error) {
      console.error(`[BloqueosService] Error al bloquear usuario ${bloqueadoUid}`, error);
      throw error;
    }
  }

  async desbloquear(bloqueadoUid: string): Promise<void> {
    const uid = this.authService.uid;
    if (!uid) throw new Error('Usuario no autenticado');

    try {
      await this.firestoreService.deleteDoc(`usuarios/${uid}/bloqueados`, bloqueadoUid);
      await this.firestoreService.deleteDoc(`usuarios/${bloqueadoUid}/bloqueadoPor`, uid);
      await this.estadisticasService.incrementarCampo('totalBloqueos', -1);
    } catch (error) {
      console.error(`[BloqueosService] Error al desbloquear usuario ${bloqueadoUid}`, error);
      throw error;
    }
  }

  async estaBloqueado(bloqueadoUid: string): Promise<boolean> {
    const uid = this.authService.uid;
    if (!uid) throw new Error('Usuario no autenticado');

    try {
      const doc = await this.firestoreService.getDocById<{ bloqueadoEn: Timestamp }>(
        `usuarios/${uid}/bloqueados`,
        bloqueadoUid
      );
      return !!doc;
    } catch (error) {
      console.error(`[BloqueosService] Error al verificar si está bloqueado: ${bloqueadoUid}`, error);
      return false;
    }
  }

  async fueBloqueadoPor(otroUid: string): Promise<boolean> {
    const uid = this.authService.uid;
    if (!uid) throw new Error('Usuario no autenticado');

    try {
      const doc = await this.firestoreService.getDocById<{ bloqueadoEn: Timestamp }>(
        `usuarios/${uid}/bloqueadoPor`,
        otroUid
      );
      return !!doc;
    } catch (error) {
      console.error(`[BloqueosService] Error al verificar si fue bloqueado por: ${otroUid}`, error);
      return false;
    }
  }

  async obtenerBloqueados(): Promise<UsuarioBloqueado[]> {
    const uid = this.authService.uid;
    if (!uid) throw new Error('Usuario no autenticado');

    try {
      const bloqueadosData = await this.firestoreService.getCollection<{ bloqueadoEn: Timestamp }>(
        `usuarios/${uid}/bloqueados`
      );

      const resultados = await Promise.all(
        bloqueadosData.map(async b => {
          const perfil = await this.userService.getPerfil(b.uid);
          return {
            uid: b.uid,
            nombre: perfil?.nombre ?? 'Desconocido',
            foto: perfil?.fotos?.find(f => !f.eliminada)?.url ??  '',
            bloqueadoEn: b.bloqueadoEn
          };
        })
      );

      return resultados;
    } catch (error) {
      console.error('[BloqueosService] Error al obtener bloqueados', error);
      throw error;
    }
  }

  async obtenerBloqueadoPor(): Promise<UsuarioBloqueado[]> {
    const uid = this.authService.uid;
    if (!uid) throw new Error('Usuario no autenticado');

    try {
      const bloqueadoPorData = await this.firestoreService.getCollection<{
        bloqueadoEn: Timestamp;
        creadoPor: string;
      }>(`usuarios/${uid}/bloqueadoPor`);

      const resultados = await Promise.all(
        bloqueadoPorData.map(async b => {
          const perfil = await this.userService.getPerfil(b.uid);
          return {
            uid: b.uid,
            nombre: perfil?.nombre ?? 'Desconocido',
            foto: perfil?.fotos?.find(f => !f.eliminada)?.url ??  '',
            bloqueadoEn: b.bloqueadoEn
          };
        })
      );

      return resultados;
    } catch (error) {
      console.error('[BloqueosService] Error al obtener bloqueadores', error);
      throw error;
    }
  }
}
